import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import ScrollToTop from "./components/ScrollToTop";
import { useContentProtection } from "./hooks/useContentProtection";
import Layout from "./components/Layout";
import Index from "./pages/Index";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

// Lazy-loaded public pages (not needed for initial render)
const About = lazy(() => import("./pages/About"));
const InvestmentStrategy = lazy(() => import("./pages/InvestmentStrategy"));
const Governance = lazy(() => import("./pages/Governance"));
const Projects = lazy(() => import("./pages/Projects"));
const InvestorInquiry = lazy(() => import("./pages/InvestorInquiry"));
const Insights = lazy(() => import("./pages/Insights"));
const Contact = lazy(() => import("./pages/Contact"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const GoldNews = lazy(() => import("./pages/GoldNews"));
const GoldArticleDetail = lazy(() => import("./pages/GoldArticleDetail"));
const WhyGold = lazy(() => import("./pages/WhyGold"));
const Leadership = lazy(() => import("./pages/Leadership"));
const Subsidiaries = lazy(() => import("./pages/Subsidiaries"));
const Partners = lazy(() => import("./pages/Partners"));
const MiningPortfolio = lazy(() => import("./pages/MiningPortfolio"));
const CompanyNews = lazy(() => import("./pages/CompanyNews"));
const CompanyNewsDetail = lazy(() => import("./pages/CompanyNewsDetail"));

// Lazy-loaded admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const LeadsManager = lazy(() => import("./pages/admin/LeadsManager"));
const CmsManager = lazy(() => import("./pages/admin/CmsManager"));
const SiteSettings = lazy(() => import("./pages/admin/SiteSettings"));
const AdminUserManager = lazy(() => import("./pages/admin/AdminUserManager"));
const AdminAccountSettings = lazy(() => import("./pages/admin/AdminAccountSettings"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const AdminGoldNews = lazy(() => import("./pages/admin/AdminGoldNews"));
const AdminLeadership = lazy(() => import("./pages/admin/AdminLeadership"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminCompanyNews = lazy(() => import("./pages/admin/AdminCompanyNews"));
const AdminContactMessages = lazy(() => import("./pages/admin/AdminContactMessages"));

// Lazy-loaded finance pages
const FinanceLayout = lazy(() => import("./components/finance/FinanceLayout"));
const FinanceLogin = lazy(() => import("./pages/finance/FinanceLogin"));
const MfaEnroll = lazy(() => import("./pages/finance/MfaEnroll"));
const MfaVerify = lazy(() => import("./pages/finance/MfaVerify"));
const FinanceDashboard = lazy(() => import("./pages/finance/FinanceDashboard"));
const FundSettings = lazy(() => import("./pages/finance/FundSettings"));
const ChartOfAccounts = lazy(() => import("./pages/finance/ChartOfAccounts"));
const JournalEntries = lazy(() => import("./pages/finance/JournalEntries"));
const JournalEntryDetail = lazy(() => import("./pages/finance/JournalEntryDetail"));
const AccountingPeriods = lazy(() => import("./pages/finance/AccountingPeriods"));
const InvestorList = lazy(() => import("./pages/finance/InvestorList"));
const InvestorDetail = lazy(() => import("./pages/finance/InvestorDetail"));
const CapitalCalls = lazy(() => import("./pages/finance/CapitalCalls"));
const Distributions = lazy(() => import("./pages/finance/Distributions"));
const ProjectList = lazy(() => import("./pages/finance/ProjectList"));
const NavDashboard = lazy(() => import("./pages/finance/NavDashboard"));
const WaterfallCalculator = lazy(() => import("./pages/finance/WaterfallCalculator"));
const BankReconciliation = lazy(() => import("./pages/finance/BankReconciliation"));
const TrialBalance = lazy(() => import("./pages/finance/TrialBalance"));
const GeneralLedger = lazy(() => import("./pages/finance/GeneralLedger"));
const FinancialStatements = lazy(() => import("./pages/finance/FinancialStatements"));
const DocumentRepository = lazy(() => import("./pages/finance/DocumentRepository"));
const TaxWorksheets = lazy(() => import("./pages/finance/TaxWorksheets"));
const ExchangeRates = lazy(() => import("./pages/finance/ExchangeRates"));
const UserGuide = lazy(() => import("./pages/finance/UserGuide"));
const CashFlow = lazy(() => import("./pages/finance/CashFlow"));
const AccountStatement = lazy(() => import("./pages/finance/AccountStatement"));
const AgingReports = lazy(() => import("./pages/finance/AgingReports"));
const FixedAssets = lazy(() => import("./pages/finance/FixedAssets"));
const DepreciationSchedules = lazy(() => import("./pages/finance/DepreciationSchedules"));
const Invoices = lazy(() => import("./pages/finance/Invoices"));

const queryClient = new QueryClient();

const App = () => {
  useContentProtection();
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/:lang" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="strategy" element={<InvestmentStrategy />} />
              <Route path="governance" element={<Governance />} />
              <Route path="projects" element={<Projects />} />
              <Route path="investor" element={<InvestorInquiry />} />
              <Route path="insights" element={<Insights />} />
              <Route path="contact" element={<Contact />} />
              <Route path="gold-news" element={<GoldNews />} />
              <Route path="gold-news/:id" element={<GoldArticleDetail />} />
              <Route path="why-gold" element={<WhyGold />} />
              <Route path="leadership" element={<Leadership />} />
              <Route path="subsidiaries" element={<Subsidiaries />} />
              <Route path="partners" element={<Partners />} />
              <Route path="mining-portfolio" element={<MiningPortfolio />} />
              <Route path="company-news" element={<CompanyNews />} />
              <Route path="company-news/:id" element={<CompanyNewsDetail />} />
              <Route path="legal" element={<LegalPage type="legal" />} />
              <Route path="privacy" element={<LegalPage type="privacy" />} />
              <Route path="terms" element={<LegalPage type="terms" />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="leads" element={<LeadsManager />} />
              <Route path="cms" element={<CmsManager />} />
              <Route path="settings" element={<SiteSettings />} />
              <Route path="users" element={<AdminUserManager />} />
              <Route path="account" element={<AdminAccountSettings />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="gold-news" element={<AdminGoldNews />} />
              <Route path="leadership" element={<AdminLeadership />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="company-news" element={<AdminCompanyNews />} />
              <Route path="contact-messages" element={<AdminContactMessages />} />
            </Route>
            {/* Finance module */}
            <Route path="/finance/login" element={<FinanceLogin />} />
            <Route path="/finance/mfa-enroll" element={<MfaEnroll />} />
            <Route path="/finance/mfa-verify" element={<MfaVerify />} />
            <Route path="/finance" element={<FinanceLayout />}>
              <Route index element={<FinanceDashboard />} />
              <Route path="settings" element={<FundSettings />} />
              <Route path="coa" element={<ChartOfAccounts />} />
              <Route path="journal" element={<JournalEntries />} />
              <Route path="journal/:id" element={<JournalEntryDetail />} />
              <Route path="periods" element={<AccountingPeriods />} />
              <Route path="investors" element={<InvestorList />} />
              <Route path="investors/:id" element={<InvestorDetail />} />
              <Route path="capital-calls" element={<CapitalCalls />} />
              <Route path="distributions" element={<Distributions />} />
              <Route path="projects" element={<ProjectList />} />
              <Route path="nav" element={<NavDashboard />} />
              <Route path="waterfall" element={<WaterfallCalculator />} />
              <Route path="bank" element={<BankReconciliation />} />
              <Route path="trial-balance" element={<TrialBalance />} />
              <Route path="general-ledger" element={<GeneralLedger />} />
              <Route path="statements" element={<FinancialStatements />} />
              <Route path="exchange-rates" element={<ExchangeRates />} />
              <Route path="documents" element={<DocumentRepository />} />
              <Route path="tax" element={<TaxWorksheets />} />
              <Route path="cash-flow" element={<CashFlow />} />
              <Route path="account-statement" element={<AccountStatement />} />
              <Route path="aging" element={<AgingReports />} />
              <Route path="assets" element={<FixedAssets />} />
              <Route path="depreciation" element={<DepreciationSchedules />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="guide" element={<UserGuide />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
